import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Avatar,
  CardContent,
  CardHeader,
  IconButton,
  ListItem,
  TextField,
  Theme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { findFeatureById } from "../functions/findFeatureById";
import { findLayerByTitle } from "../functions/findLayerByTitle";
import {
  clearSeaSurveysHoverStyle,
  setSeaSurveyHoverStyle,
} from "../hooks/useSeaSurveyEventHandler";
import { LayerTitle } from "../types/layers";
import { MapItem } from "../types/mapItems";

type Props = {
  map: Map | undefined;
  items: MapItem[];
  onItemSelect: (item: MapItem) => void;
};

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(10),
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  content: {
    maxHeight: 300,
    minWidth: 300,
    overflowY: "auto",
  },
}));

export const MultiplePopup = ({
  map,
  items,
  onItemSelect,
}: Props): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation("map");

  return (
    <>
      <CardHeader
        avatar={<Avatar className={classes.avatar}>{items.length}</Avatar>}
        title={t("select-item")}
        subheader=""
        titleTypographyProps={{ noWrap: true, title: "title" }}
        subheaderTypographyProps={{ noWrap: true, title: "subheader" }}
        action={
          <IconButton
            onClick={() => {
              const ol = map?.getOverlayById("popup");
              ol?.setPosition(undefined);
              ol?.set("clickedOpen", false);
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        }
      />
      <CardContent className={classes.content}>
        <Autocomplete
          options={items}
          getOptionLabel={(opt) => opt.name ?? t("general:unknown")}
          renderInput={(params) => <TextField {...params} variant="outlined" />}
          openOnFocus
          onChange={(_e, v) => {
            if (v) {
              onItemSelect(v);
            }
          }}
          renderOption={(params, opt) => (
            <ListItem
              {...params}
              onMouseEnter={() => {
                if (opt.type !== "measurement") return;
                const layer = findLayerByTitle(map, LayerTitle.Measurements);
                if (!map || !layer) return;
                const feat = findFeatureById(map, opt.mapId);
                if (!feat) return;
                clearSeaSurveysHoverStyle(map);
                setSeaSurveyHoverStyle(feat, layer, map);
              }}
            >
              {opt.name ?? t("general:unknown")}
            </ListItem>
          )}
        />
      </CardContent>
    </>
  );
};
